import React, { useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { Table, Button, Modal, Select, Spin, Alert, Badge, Tag, Row, Col, Card, Space } from "antd";
import { ExclamationCircleOutlined, CheckCircleOutlined,
  InfoCircleOutlined, PlusCircleOutlined, MinusCircleFilled, EditOutlined, ReloadOutlined, TeamOutlined
 } from "@ant-design/icons";
import { useMatches, useCreateMatch, useUpdateMatch, useDeleteMatch } from "../../hooks/useMatches";
import { createFrame } from "../../api/services/frameService";
import AddFrameModal from "../Frame/AddFrameModal";
import AddMatchModal from "./AddMatchModal";
import EditMatchModal from "./EditMatchModal";
import { Tooltip, message } from "antd";
import "antd/dist/reset.css"; // Für Ant Design v5


const MatchList = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  // React Query Hooks
  const { data: matches = [], isLoading, error, refetch } = useMatches();
  const createMatchMutation = useCreateMatch();
  const updateMatchMutation = useUpdateMatch();
  const deleteMatchMutation = useDeleteMatch();

  // Local State
  const [isAddFrameModalVisible, setIsAddFrameModalVisible] = useState(false);
  const [selectedMatchForFrame, setSelectedMatchForFrame] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [filter, setFilter] = useState("user_involved");

  // Filtered Matches mit useMemo für Performance
  const filteredMatches = useMemo(() => {
    console.log('=== FILTER DEBUG ===');
    console.log('Current filter:', filter);
    console.log('Current user:', currentUser);
    console.log('Total matches:', matches.length);

    const result = matches.filter((match) => {
      // Standard Filter ohne User-Beteiligung
      if (filter === "finished") return match.is_finished;
      if (filter === "unfinished") return !match.is_finished;
      if (filter === "practice") return match.is_practice_match;
      if (filter === "open_end") return match.is_open_end;

      // Team-Match Filter
      if (filter === "team_matches") return match.is_team_match;

      // User-basierte Filter
      if (filter === "user_involved") {
        if (!currentUser || !currentUser.player_id) {
          console.log('No current user or player_id for filter');
          return false;
        }

        const playerId = String(currentUser.player_id);

        // Bei Team-Matches: Prüfen ob User in einem der Teams ist
        if (match.is_team_match) {
          const allPlayerIds = match.all_players?.map(p => String(p.id)) || [];
          return allPlayerIds.includes(playerId);
        }

        // Bei Einzel-Matches: wie bisher
        const player1Id = String(match.player1);
        const player2Id = String(match.player2);

        const isInvolved = player1Id === playerId || player2Id === playerId;

        console.log(`Match ${match.id}: player1=${player1Id}, player2=${player2Id}, playerId=${playerId}, involved=${isInvolved}`);

        return isInvolved;
      }

      if (filter === "user_not_involved") {
        if (!currentUser || !currentUser.player_id) return true;

        const playerId = String(currentUser.player_id);

        // Bei Team-Matches: Prüfen ob User NICHT in einem der Teams ist
        if (match.is_team_match) {
          const allPlayerIds = match.all_players?.map(p => String(p.id)) || [];
          return !allPlayerIds.includes(playerId);
        }

        // Bei Einzel-Matches: wie bisher
        const player1Id = String(match.player1);
        const player2Id = String(match.player2);

        const notInvolved = player1Id !== playerId && player2Id !== playerId;

        console.log(`Match ${match.id}: player1=${player1Id}, player2=${player2Id}, playerId=${playerId}, notInvolved=${notInvolved}`);

        return notInvolved;
      }

      return true;
    });

    console.log('Filtered matches count:', result.length);
    console.log('===================');

    return result;
  }, [matches, filter, currentUser]);

  const toggleModal = () => {
    setIsModalVisible((prev) => !prev);
  };

  // CREATE Match Handler mit React Query
  const handleCreateMatch = (matchData) => {
    createMatchMutation.mutate(matchData, {
      onSuccess: () => {
        message.success("Match erfolgreich erstellt!");
        setIsModalVisible(false);
      },
      onError: (error) => {
        // Besseres Error Handling: API-Fehlermeldung anzeigen
        const errorMessage = error.response?.data?.player2
          || error.response?.data?.detail
          || error.message
          || "Fehler beim Erstellen des Matches";
        message.error(errorMessage);
      },
    });
  };

  // UPDATE Match Handler mit React Query
  const handleUpdateMatch = (matchId, matchData) => {
    updateMatchMutation.mutate({ matchId, matchData }, {
      onSuccess: () => {
        message.success("Match erfolgreich aktualisiert!");
        setIsEditModalVisible(false);
        setSelectedMatch(null);
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.detail
          || error.message
          || "Fehler beim Aktualisieren des Matches";
        message.error(errorMessage);
      },
    });
  };
  // DELETE Match Handler mit React Query
  const handleDeleteMatch = (match) => {
    const maxScore = Math.max(match.player1_score, match.player2_score);
    const remainingFrames = Math.max(0, match.race_to - maxScore);

    Modal.confirm({
      title: "Match löschen bestätigen",
      icon: <ExclamationCircleOutlined style={{ color: "red" }} />,
      content: (
        <div>
          <p>
            Sind Sie sicher, dass Sie das folgende Match löschen möchten? Dies
            wird auch alle zugehörigen Frames löschen.
          </p>
          <ul>
            <li>
              <strong>Spieler:</strong> {match.player1_name} vs. {match.player2_name}
            </li>
            <li>
              <strong>Spiel-Typ:</strong> {match.game_type_name}
            </li>
            <li>
              <strong>Gespielte Frames:</strong> {match.frames?.length || 0}
            </li>
            <li>
              <strong>Offene Frames:</strong> {remainingFrames}
            </li>
          </ul>
        </div>
      ),
      okText: "Ja, löschen",
      okType: "danger",
      cancelText: "Abbrechen",
      onOk: () => {
        deleteMatchMutation.mutate(match.id, {
          onSuccess: () => {
            message.success("Match erfolgreich gelöscht!");
          },
          onError: (error) => {
            const errorMessage = error.response?.data?.detail
              || error.message
              || "Fehler beim Löschen des Matches";
            message.error(errorMessage);
          },
        });
      },
    });
  };
  const handleAddFrame = async (frameData) => {
    try {
      // Validate selectedMatchForFrame
      if (!selectedMatchForFrame) {
        console.error("selectedMatchForFrame is null!");
        message.error("Match-Auswahl fehlt. Bitte versuchen Sie es erneut.");
        return;
      }

      // Ensure match ID is set
      if (!selectedMatchForFrame.id) {
        console.error("selectedMatchForFrame.id is null!", selectedMatchForFrame);
        message.error("Match-ID fehlt. Bitte versuchen Sie es erneut.");
        return;
      }

      // Calculate loser based on match type
      let loser;
      if (selectedMatchForFrame.is_team_match) {
        // Bei Team-Matches: Winner ist aus einem Team, Loser ist ein Spieler aus dem anderen Team
        const team1PlayerIds = selectedMatchForFrame.team1_players?.map(p => p.id) || [];
        const team2PlayerIds = selectedMatchForFrame.team2_players?.map(p => p.id) || [];

        if (team1PlayerIds.includes(frameData.winner)) {
          // Winner aus Team 1 -> Loser aus Team 2 (erster Spieler als Default)
          loser = team2PlayerIds[0];
        } else {
          // Winner aus Team 2 -> Loser aus Team 1 (erster Spieler als Default)
          loser = team1PlayerIds[0];
        }
      } else {
        // Bei Einzel-Matches: der andere Spieler ist der Loser
        loser = frameData.winner === selectedMatchForFrame.player1
          ? selectedMatchForFrame.player2
          : selectedMatchForFrame.player1;
      }

      // Build complete frame data with match ID and defaults
      const completeFrameData = {
        winner: frameData.winner,
        loser: loser,
        match: selectedMatchForFrame.id,
        loser_remaining_balls: frameData.loser_remaining_balls ?? 0,
        eight_ball_flag: frameData.eight_ball_flag ?? false,
        won_on_break: frameData.won_on_break ?? false,
        // Feature #24: Break-Tracking
        breaking_player: frameData.breaking_player ?? null,
        // Feature #23: Foul-Tracking
        winner_fouls: frameData.winner_fouls ?? 0,
        loser_fouls: frameData.loser_fouls ?? 0
      };

      console.log("Sending frame data to API:", completeFrameData);
      console.log("Selected match:", selectedMatchForFrame);

      const data = await createFrame(completeFrameData);
      console.info("Frame successfully created:", data);

      message.success("Frame erfolgreich hinzugefügt!");

      // Matches neu laden mit React Query refetch
      await refetch();

      // Modal schließen
      setIsAddFrameModalVisible(false);
      setSelectedMatchForFrame(null);
    } catch (error) {
      console.error("Error adding frame:", error);
      console.error("Error response:", error.response?.data);

      const errorMessage = error.response?.data?.detail
        || error.response?.data?.match?.[0]
        || error.message
        || "Fehler beim Hinzufügen des Frames";
      message.error(errorMessage);
    }
  };

  const openAddFrameModal = (match) => {
    setSelectedMatchForFrame(match);
    setIsAddFrameModalVisible(true);
  };

  const closeAddFrameModal = () => {
    setIsAddFrameModalVisible(false);
    setSelectedMatchForFrame(null);
  };

  const columns = [
      {
      title: "Status",
      key: "status",
      render: (text, record) => {
        if (record.is_open_end) {
          return <InfoCircleOutlined style={{ color: "blue", fontSize: "18px" }} />;
        }
        const maxScore = Math.max(record.player1_score, record.player2_score);
        const remainingFrames = Math.max(0, record.race_to - maxScore);

        if (remainingFrames > 0) {
          return <ExclamationCircleOutlined style={{ color: "orange", fontSize: "18px" }} />;
        } else {
          return <CheckCircleOutlined style={{ color: "green", fontSize: "18px" }} />;
        }
      },
    },
    {
      title: "Typ",
      key: "type",
      render: (text, record) => (
        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
          {record.is_team_match && <Tag color="cyan" icon={<TeamOutlined />}>Team</Tag>}
          {record.is_open_end && <Tag color="blue">Open End</Tag>}
          {record.is_practice_match && <Tag color="purple">Übung</Tag>}
        </div>
      ),
    },
    // { title: "Match ID", dataIndex: "id", key: "id" },
    {
      title: "Scores",
      key: "player_vs_points",
      render: (text, record) => {
        if (record.is_team_match) {
          // Team-Match: Team-Namen anzeigen
          const team1Name = record.team1_name || 'Team 1';
          const team2Name = record.team2_name || 'Team 2';
          return `${team1Name} ${record.team1_score || 0} : ${record.team2_score || 0} ${team2Name}`;
        }
        // Einzel-Match: player_vs_points verwenden
        return record.player_vs_points;
      }
    },

    { title: "Game Type", dataIndex: "game_type_name", key: "game_type_name" },
    {
      title: "Race To",
      key: "race_to",
      render: (text, record) => record.is_open_end ? "-" : (record.race_to || "-")
    },
    { title: "Frames Played", key: "frames_played",
      render: (text, record) => record.frames?.length || 0, // Anzahl der Frames
    },
   
    {
  title: "Remaining Frames",
  key: "remaining_frames",
  render: (text, record) => {
    if (record.is_open_end) return "∞";
    const maxScore = Math.max(record.player1_score, record.player2_score);
    return Math.max(0, record.race_to - maxScore); // Verbleibende Frames
  },
},
    { title: "Game Date", dataIndex: "game_date", key: "game_date" },

     {
  title: "Actions",
  key: "actions",
  render: (text, record) => {
    const players = [
      { id: record.player1, name: record.player1_name },
      { id: record.player2, name: record.player2_name },
    ];
    const isEightBall = record.game_type_name === "8-Ball";

    // Verbleibende Frames berechnen
    const maxScore = Math.max(record.player1_score, record.player2_score);
    const remainingFrames = Math.max(0, record.race_to - maxScore);

    return (
      <div style={{ display: "flex", gap: "8px" }}>
        {/* Match-Details Button */}
        <Tooltip title="Match-Details">
          <InfoCircleOutlined
            style={{ fontSize: "18px", cursor: "pointer", color: "#1890ff" }}
             onClick={() => navigate(`/match/${record.id}`)}
          />
        </Tooltip>

        {/* Match bearbeiten Button - nur bei unfertigen Matches */}
        {!record.is_finished && (
          <Tooltip title="Match bearbeiten">
            <EditOutlined
              style={{ fontSize: "18px", cursor: "pointer", color: "#faad14" }}
              onClick={() => {
                setSelectedMatch(record);
                setIsEditModalVisible(true);
              }}
            />
          </Tooltip>
        )}

        {/* Frame hinzufügen Button - auch bei Open-End Matches */}
        {!record.is_finished && (remainingFrames > 0 || record.is_open_end) && (
          <Tooltip title="Frame hinzufügen">
            <PlusCircleOutlined
              style={{ fontSize: "18px", cursor: "pointer", color: "#52c41a" }}
              onClick={() => openAddFrameModal(record)}
            />
          </Tooltip>
        )}
        {/* Match löschen Button (optional) */}
         <Tooltip title="Match löschen">
          <MinusCircleFilled
            style={{ fontSize: "18px", cursor: "pointer", color: "#ff4d4f" }}
            onClick={() => {
              handleDeleteMatch(record);
            }}
          />
        </Tooltip>
      </div>
    );
  },
     }

  ];

  if (isLoading) {
    return <Spin size="large" style={{ display: 'block', margin: '50px auto' }} />;
  }

  if (error) {
    return <Alert
      type="error"
      message="Fehler beim Laden der Matches"
      description={error.message || "Ein unbekannter Fehler ist aufgetreten"}
      style={{ margin: '20px' }}
    />;
  }

  // Render Match Card für Mobile
  const renderMatchCard = (match) => {
    const players = match.is_team_match
      ? match.all_players || []
      : [
          { id: match.player1, name: match.player1_name },
          { id: match.player2, name: match.player2_name },
        ];
    const isEightBall = match.game_type_name === "8-Ball";
    const maxScore = match.is_team_match
      ? Math.max(match.team1_score || 0, match.team2_score || 0)
      : Math.max(match.player1_score, match.player2_score);
    const remainingFrames = Math.max(0, match.race_to - maxScore);

    // Status Icon
    let statusIcon;
    let statusColor;
    if (match.is_open_end) {
      statusIcon = <InfoCircleOutlined />;
      statusColor = "blue";
    } else if (remainingFrames > 0) {
      statusIcon = <ExclamationCircleOutlined />;
      statusColor = "orange";
    } else {
      statusIcon = <CheckCircleOutlined />;
      statusColor = "green";
    }

    // Score-Anzeige für Team-Matches
    const scoreDisplay = match.is_team_match
      ? `${match.team1_name || 'Team 1'} ${match.team1_score || 0} : ${match.team2_score || 0} ${match.team2_name || 'Team 2'}`
      : match.player_vs_points;

    return (
      <Card
        key={match.id}
        size="small"
        style={{ marginBottom: '12px' }}
        extra={
          <Space>
            {statusIcon && <span style={{ color: statusColor, fontSize: '18px' }}>{statusIcon}</span>}
          </Space>
        }
      >
        {/* Match Info */}
        <div style={{ marginBottom: '8px' }}>
          <strong style={{ fontSize: '16px' }}>{scoreDisplay}</strong>
        </div>

        {/* Game Type & Tags */}
        <Space wrap style={{ marginBottom: '8px' }}>
          <Tag color="blue">{match.game_type_name}</Tag>
          {match.is_team_match && <Tag color="cyan" icon={<TeamOutlined />}>Team</Tag>}
          {match.is_open_end && <Tag color="blue">Open End</Tag>}
          {match.is_practice_match && <Tag color="purple">Übung</Tag>}
        </Space>

        {/* Match Details */}
        <Row gutter={[8, 4]} style={{ fontSize: '13px', marginBottom: '8px' }}>
          <Col span={12}>
            <strong>Race To:</strong> {match.is_open_end ? "-" : (match.race_to || "-")}
          </Col>
          <Col span={12}>
            <strong>Frames:</strong> {match.frames?.length || 0}
          </Col>
          <Col span={12}>
            <strong>Verbleibend:</strong> {match.is_open_end ? "∞" : remainingFrames}
          </Col>
          <Col span={12}>
            <strong>Datum:</strong> {match.game_date}
          </Col>
        </Row>

        {/* Actions */}
        <Space wrap>
          <Tooltip title="Match-Details">
            <Button
              size="small"
              icon={<InfoCircleOutlined />}
              onClick={() => navigate(`/match/${match.id}`)}
            >
              Details
            </Button>
          </Tooltip>

          {!match.is_finished && (
            <Tooltip title="Match bearbeiten">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedMatch(match);
                  setIsEditModalVisible(true);
                }}
              >
                Bearbeiten
              </Button>
            </Tooltip>
          )}

          {!match.is_finished && (remainingFrames > 0 || match.is_open_end) && (
            <Tooltip title="Frame hinzufügen">
              <Button
                size="small"
                icon={<PlusCircleOutlined />}
                type="primary"
                onClick={() => openAddFrameModal(match)}
              >
                Frame
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Match löschen">
            <Button
              size="small"
              danger
              icon={<MinusCircleFilled />}
              onClick={() => handleDeleteMatch(match)}
            >
              Löschen
            </Button>
          </Tooltip>
        </Space>
      </Card>
    );
  };

  return (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden'
  }}>
      {/* Feature #21: Fixed Header - Button + Filter bleiben immer oben */}
      <div style={{
        flex: '0 0 auto',
        padding: '16px',
        backgroundColor: '#fff',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Button type="primary" onClick={() => toggleModal()} block>
              Match anlegen
            </Button>
          </Col>
          <Col xs={24} sm={12} md={14}>
            <Select
              value={filter}
              onChange={(value) => setFilter(value)}
              style={{ width: "100%" }}
            >
              <Select.Option value="all">Alle Matches</Select.Option>
              <Select.Option value="user_involved">Meine Matches</Select.Option>
              <Select.Option value="user_not_involved">Matches ohne Beteiligung</Select.Option>
              <Select.Option value="finished">Fertige Matches</Select.Option>
              <Select.Option value="unfinished">Unfertige Matches</Select.Option>
              <Select.Option value="team_matches">Team-Matches</Select.Option>
              <Select.Option value="practice">Übungsspiele</Select.Option>
              <Select.Option value="open_end">Open End Matches</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
              block
            >
              Aktualisieren
            </Button>
          </Col>
        </Row>
      </div>

      <AddMatchModal
        open={isModalVisible}
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onCreateMatch={handleCreateMatch}
      />
      <EditMatchModal
        visible={isEditModalVisible}
        onClose={() => {
          setIsEditModalVisible(false);
          setSelectedMatch(null);
        }}
        onUpdateMatch={handleUpdateMatch}
        match={selectedMatch}
      />

      <AddFrameModal
        visible={isAddFrameModalVisible}
        onClose={closeAddFrameModal}
        onAddFrame={handleAddFrame}
        players={selectedMatchForFrame ? (
          selectedMatchForFrame.is_team_match
            ? selectedMatchForFrame.all_players || []
            : [
                { id: selectedMatchForFrame.player1, name: selectedMatchForFrame.player1_name },
                { id: selectedMatchForFrame.player2, name: selectedMatchForFrame.player2_name }
              ]
        ) : []}
        isEightBall={selectedMatchForFrame?.game_type_name === "8-Ball"}
        isTeamMatch={selectedMatchForFrame?.is_team_match || false}
        team1Players={selectedMatchForFrame?.team1_players || []}
        team2Players={selectedMatchForFrame?.team2_players || []}
        team1Name={selectedMatchForFrame?.team1_name}
        team2Name={selectedMatchForFrame?.team2_name}
      />

      {/* Feature #21: Scrollable Match List Area - NUR die Liste scrollt */}
      <div style={{
        flex: '1 1 auto',
        overflowY: 'auto',
        padding: '16px'
      }}>
        {/* Mobile View: Card List */}
        <div style={{ display: 'block' }} className="mobile-card-list">
          <style>{`
            @media (min-width: 768px) {
              .mobile-card-list {
                display: none !important;
              }
            }
          `}</style>
          {filteredMatches.map(renderMatchCard)}
        </div>

        {/* Desktop View: Table */}
        <div style={{ display: 'none' }} className="desktop-table">
          <style>{`
            @media (min-width: 768px) {
              .desktop-table {
                display: block !important;
              }
            }
          `}</style>
          <Table
            dataSource={filteredMatches}
            columns={columns}
            rowKey={(record) => record.id || `temp-${Math.random()}`}
          />
        </div>
      </div>
</div>
);
};

export default MatchList;